import { useRouter } from 'next/router';
import { cn } from '@poseidon/components/cn';
import useLocalStorage from '@rehooks/local-storage';
import { AnimatePresence, motion } from 'framer-motion';

import type { SvrWithTeamApiEnabledFlags, SvrWTeamLogo } from '@poseidon/types/svr/svrTeam.type';
import type { SvrUserTeamBase } from '@poseidon/types/svr/svrUserTeam.type';
import {
  Charts,
  Communication,
  Development,
  Editor,
  Files,
  General,
  Layout,
  Maps,
  Time,
  Users,
} from '@poseidon/components/StrokeIcons';

import { Kbd } from '@/components/Kbd';
import { isMacLike, useKey } from '@/hooks/useKey';
import { api } from '@/utils/api';
import { LeftNavRow } from '~/components/LeftNav/LeftNavRow';
import { LogoRow } from '~/components/LeftNav/LogoRow';
import { dataOnboardingIds } from '~/components/Onboarding/onboarding.model';
import { UserLogoutRow } from '~/components/UserLogoutRow';
import styles from './LeftNav.module.scss';

export const LeftNav = <
  AuthData extends SvrUserTeamBase & SvrWTeamLogo & SvrWithTeamApiEnabledFlags & { pendingOneOffStepsCount: number },
>(props: {
  userTeam: AuthData;
}) => {
  const router = useRouter();
  const [navMinimized, setNavMinimized] = useLocalStorage('navMinimized', false);

  const countHubspotResults = api.hubspot.getDuplicatesCount.useQuery(
    { teamId: props.userTeam.teamId },
    { enabled: props.userTeam.hubspotConnected == 'success' },
  );

  useKey('b', () => setNavMinimized(!navMinimized), { metaWindowsCtrlKey: true });
  useKey(
    'k',
    (e) => {
      // prevent default browser ctrl+k shortcut
      e.preventDefault();

      const page = '/prospects';
      if (router.pathname === page) {
        // if we are already on the page, then just focus on input
        const searchInput = document.getElementById('search_input');
        searchInput?.focus();
      } else {
        void router.replace(page);
      }
    },
    { metaWindowsCtrlKey: true },
  );

  return (
    <AnimatePresence>
      <motion.div
        className={cn(styles.container, navMinimized ? styles.minimized : styles.maximized)}
        transition={{ type: 'spring' }}>
        <LogoRow navMinimized={navMinimized} setNavMinimized={setNavMinimized} />

        <div data-intro="Things!" className={styles.links}>
          <LeftNavRow
            href="/leads"
            icon={<General.HomeSmile />}
            minimized={navMinimized}
            data-onboarding={dataOnboardingIds.takeActionContactsTrigger}>
            Take Action
          </LeftNavRow>
          <LeftNavRow
            href="/scheduled"
            icon={<Time.ClockCheck />}
            counterValue={props.userTeam.pendingOneOffStepsCount}
            minimized={navMinimized}>
            Scheduled
          </LeftNavRow>
          <LeftNavRow
            href="/templates"
            icon={<Maps.Rocket />}
            minimized={navMinimized}
            addLink="/templates/add"
            addLinkLabel="Add Campaign"
            data-onboarding={dataOnboardingIds.campaignTemplatesTrigger}>
            Campaigns
          </LeftNavRow>
          <LeftNavRow
            href="/prompts"
            icon={<Communication.MessageTextSquare02 />}
            minimized={navMinimized}
            addLink="/prompts/add"
            addLinkLabel="Add Message"
            data-onboarding={dataOnboardingIds.campaignTemplatesTrigger}>
            Templates
          </LeftNavRow>

          <LeftNavRow href="/prospects" icon={<Users.Users01 />} minimized={navMinimized}>
            Search <Kbd>{isMacLike() ? '⌘' : 'ctrl'}+K</Kbd>
          </LeftNavRow>
          <LeftNavRow href="/pipeline" icon={<Layout.AlignTopArrow02 />} minimized={navMinimized}>
            Pipeline
          </LeftNavRow>

          <LeftNavRow
            href="/activities"
            icon={<Charts.BarChartSquare02 />}
            minimized={navMinimized}
            subLinks={[
              // {
              //   href: '/logs',
              //   icon: <General.Archive />,
              //   children: 'Logs'
              // },
              {
                href: '/results',
                icon: <Users.UsersPlus />,
                children: 'Connection results',
              },
            ]}>
            Activities
          </LeftNavRow>
          <LeftNavRow
            href="/content"
            icon={<Files.FileAttachment02 />}
            addLink="/content?add=true"
            addLinkLabel="Add Content"
            minimized={navMinimized}>
            Content Library
          </LeftNavRow>

          <LeftNavRow href="/creator" icon={<Editor.PenTool02 />} minimized={navMinimized}>
            Creator Kit
          </LeftNavRow>

          {props.userTeam.hubspotConnected == 'success' && (
            <LeftNavRow
              href="/hubspot/duplicates"
              counterValue={countHubspotResults.data ?? undefined}
              icon={<Development.GitMerge />}
              minimized={navMinimized}>
              Sync Contacts
            </LeftNavRow>
          )}
          <div className="min-h-10" />
          <LeftNavRow href="/uploadcsv" icon={<General.UploadCloud02 />} minimized={navMinimized}>
            Upload CSV
          </LeftNavRow>
        </div>
        <div className={cn(styles.footer, 'mx-4 mb-8 shrink-0')}>
          <UserLogoutRow authData={props.userTeam} minimized={navMinimized} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
